import { AutoComplete, Button, Divider, Popover, Spin } from "antd";
import { LabelledElement } from ".";
import { useState, useEffect, useRef } from "react";
import { useDebounce } from "use-debounce";
import { ReactivateBrandModal } from "modals";
import { useModal } from "hooks";
import { getBrandFullInfo, reactivateBrandApi } from "api";

export const LabelledAutocompleteBrand = ({
    label = "Search",
    value = "",
    type = "text",
    addable = false,
    onSelect = () => { },
    onAddNew = () => { },
    onSearch = () => { },
    ...props
}) => {
    const [inputValue, setInputValue] = useState(value || "");
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const [currentBrandName, setCurrentBrandName] = useState("");
    const [brandInfo, setBrandInfo] = useState({
        product_nm: "",
        brand_nm: "",
        manufacturer_nm: "",
        sub_cat_nm: ""
    });
    const [brandInfoLoading, setBrandInfoLoading] = useState(false);
    const [debouncedBrandName] = useDebounce(currentBrandName, 500);
    
    const reactivateBrandModal = useModal();

    // Update inputValue when value prop changes
    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    const handleSelect = (value, option) => {
        setInputValue(value);
        setIsOpen(false);
        setActiveIndex(-1);
        if (onSelect) {
            setIsLoading(true);
            onSelect(value, option);
            setIsLoading(false);
        }

        if(!option.active_f){
            reactivateBrandModal.openModal();
        }
    };

    const handleSearch = (searchText) => {
        setInputValue(searchText);
        setActiveIndex(-1); // Reset active index when searching
        if (onSearch) {
            onSearch(searchText);
        }
    };

    const handleDropdownVisibleChange = (open) => {
        setIsOpen(open);
        if (!open) {
            setActiveIndex(-1);
            clearBrandInfo();
        }
    };

    const clearBrandInfo = () => {
        setCurrentBrandName("");
        setBrandInfo({
            product_nm: "",
            brand_nm: "",
            manufacturer_nm: "",
            sub_cat_nm: ""
        });
        setBrandInfoLoading(false);
    };

    const handleReactivateBrand = async () => {
        const res = await getBrandFullInfo(inputValue, false);
        if (res.success) {
          if (res.payload) {
            const response = await reactivateBrandApi({
              brand_name: res.payload.brand_name,
              sub_cat_i: res.payload?.sub_cat_i ? res.payload?.sub_cat_i : null,
              mfg_id: res.payload?.manufacturer ? (res.payload?.manufacturer.id ? res.payload?.manufacturer.id : null) : null,
            });
            
            if(!response.success) 
              handleCancel();
          }
          else 
            handleCancel();
        }
        else
          handleCancel();
    };

    const handleCancel = () => {
        setInputValue("");
        clearBrandInfo();
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen || !props.options || props.options.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(prev =>
                    prev < props.options.length - 1 ? prev + 1 : 0
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => prev > 0 ? prev - 1 : props.options.length - 1);
            } else if (e.key === 'Escape') {
                setIsOpen(false);
                setActiveIndex(-1);
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, props.options]);

    // Update current brand name when active index changes
    useEffect(() => {
        if (activeIndex >= 0 && props.options && props.options[activeIndex]) {
            const option = props.options[activeIndex];
            setCurrentBrandName(option.label || "");
        } else {
            clearBrandInfo();
        }
    }, [activeIndex, props.options]);

    // Fetch brand info when debounced brand name changes
    useEffect(() => {
        const fetchBrandInfo = async () => {
            if (!debouncedBrandName) return;

            setBrandInfoLoading(true);
            try {
                const option = props.options[activeIndex];
                const res = await getBrandFullInfo(debouncedBrandName, option.active_f);
                if (res.success && res.payload) {
                    setBrandInfo({
                        product_nm: res.payload.brand_name || "",
                        brand_nm: res.payload?.brand_group_name || "",
                        manufacturer_nm: res.payload?.manufacturer?.alt_nm || "",
                        sub_cat_nm: res.payload?.product_category?.sub_cat_nm || ""
                    });
                }
            } catch (error) {
                console.error("Error fetching brand info:", error);
            } finally {
                setBrandInfoLoading(false);
            }
        };

        fetchBrandInfo();
    }, [debouncedBrandName]);

    return (
        <LabelledElement label={label} type={type}>
            <AutoComplete
                ref={selectRef}
                value={inputValue}
                onSelect={handleSelect}
                onSearch={handleSearch}
                onChange={setInputValue}
                onDropdownVisibleChange={handleDropdownVisibleChange}
                variant="filled"
                loading={isLoading}
                notFoundContent={isLoading ? "Loading..." : "No options found"}
                popupClassName="autocomplete-multiline"
                optionRender={(option, info) => {
                    const isActive = info.index === activeIndex;

                    const popoverContent = brandInfoLoading ? (
                        <div style={{ textAlign: 'center', padding: '8px' }}>
                            <Spin size="small" />
                        </div>
                    ) : (
                        brandInfo.product_nm && (
                            <div>
                                <div>{`Product Name : ${brandInfo.product_nm}`}</div>
                                <div>{`Brand Name : ${brandInfo.brand_nm}`}</div>
                                <div>{`Manufacturer : ${brandInfo.manufacturer_nm}`}</div>
                                <div>{`Sub Category : ${brandInfo.sub_cat_nm}`}</div>
                            </div>
                        )
                    );

                    return (
                        <Popover
                            content={popoverContent}
                            placement="left"
                            trigger="manual"
                            open={isActive && (brandInfoLoading || brandInfo.product_nm)}
                        >
                            <div
                                onMouseEnter={() => setActiveIndex(info.index)}
                                className={!option.data.active_f ? "inactiveBrand" : ""}
                            >
                                <span>{option.label}</span>
                            </div>
                        </Popover>
                    );
                }}
                popupRender={(menu) => (
                    <div>
                        {menu}
                        {addable && (
                            <div className="mt-2">
                                <Divider style={{ margin: '0' }} />
                                <Button
                                    block
                                    type="text"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddNew(inputValue);
                                    }}
                                    className="mt-2">
                                    {`Add New ${label}`}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
                {...props}
            />
            <ReactivateBrandModal 
                onOk={handleReactivateBrand}
                onCancel={handleCancel}
                {...reactivateBrandModal} />
            <style jsx="true">{`
                .autocomplete-multiline .ant-select-item-option-content {
                    white-space: normal !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                    line-height: 1.4 !important;
                }
                .autocomplete-multiline .ant-select-item {
                    height: auto !important;
                    min-height: 32px !important;
                    padding: 8px 12px !important;
                }
                .inactiveBrand {    
                    color: gray !important;
                    font-style: italic !important;
                }
            `}</style>
        </LabelledElement>
    )
};